'use client';

import { useEffect } from 'react';
import styled from '@emotion/styled';
import { ContentWrap, Css, Responsive } from '@/ui';
import { css } from '@emotion/core';
import { theme } from '../../../styles/theme';
import { Shadow } from '../../../styles/Shadow';
import { Color } from '../../../styles/Color';
import { IParkingAvailability } from '../domain/models/IParkingAvailability';
import { capitalizeFirstChar } from '../../../utils/capitalizeFirstChar';
import { useGetApiData } from '../../../data/hooks/useGetApiData';
import { IParkingAvailabilityResponse } from '../domain/models/IParkingAvailabilityResponse';
import { PARKING_AVAILABILITY_API } from '../domain/api';
import ParkingAvailabilityWidgetShimmer from './ParkingAvailabilityWidgetShimmer';
import ErrorMessage from '../../shared/components/error/ErrorMessage';
import { ErrorMessages } from '../../../utils/errorMessages';

import WidgetFooter from '../../../components/common/widget/WidgetFooter';
import { getHeadingStyle } from '../../../styles/utils/getHeadingStyle';

interface IParkingAvailabilityWidgetProps {
	onError?: () => void;
}

export default function ParkingAvailabilityWidget({
	onError,
}: IParkingAvailabilityWidgetProps) {
	const { data, loading, error, refetch } =
		useGetApiData<IParkingAvailabilityResponse>(PARKING_AVAILABILITY_API);

	useEffect(() => {
		if (error) {
			onError?.();
		}
	}, [error]);

	const parkingOptionOrder: { [key: string]: number } = {
		'facility-vl': 1,
		'facility-st': 2,
		'facility-vp': 3,
		'facility-ec': 4,
	};

	const getStatus = (item: IParkingAvailability) => {
		if (item.name === 'CVG Valet') {
			return item.status ? capitalizeFirstChar(item.status) : 'No info';
		}
		if (item.status) {
			return capitalizeFirstChar(item.status);
		}
		if (item.available) {
			return item.available;
		}
		return 'No info';
	};

	const getStatusColor = (status: string | number | null) => {
		if (status === 'Open' || typeof status === 'number') {
			return {
				backgroundColor: Color.SecondaryGreen287,
				color: Color.OnSecondaryGreen2,
				dotColor: Color.Success40,
			};
		}

		if (status === 'Closed' || status === 'Limited') {
			return {
				backgroundColor: Color.Error96,
				color: Color.Error16,
				dotColor: Color.Error46,
			};
		}

		return {
			backgroundColor: Color.Neutral94,
			color: Color.Neutral30,
			dotColor: Color.Neutral30,
		};
	};

	if (loading && !error) {
		return <ParkingAvailabilityWidgetShimmer />;
	}

	if (error || !data) {
		return (
			<WidgetErrorMessageWrap>
				<ErrorMessage message={ErrorMessages.GenericError} />
			</WidgetErrorMessageWrap>
		);
	}

	const sortedParkingOptions = data.data.parkingAvailability
		.slice()
		.sort((a, b) => {
			const orderA = parkingOptionOrder[a.id];
			const orderB = parkingOptionOrder[b.id];
			return orderA - orderB;
		});

	return (
		<ContentWrap>
			<ParkingWidgetContainer>
				<WidgetTitle>Open Parking Spots</WidgetTitle>
				<WidgetContent>
					{sortedParkingOptions.map((item: IParkingAvailability) => {
						const status = getStatus(item);
						const statusColors = getStatusColor(status);
						return (
							<WidgetContentBlock key={item.id}>
								{item.name}{' '}
								<InfoBlock {...statusColors}>
									{status} <InfoDot {...statusColors} />{' '}
								</InfoBlock>
							</WidgetContentBlock>
						);
					})}
				</WidgetContent>
			</ParkingWidgetContainer>
			<WidgetFooter refetch={refetch} label="Refresh Open Parking Spots" />
		</ContentWrap>
	);
}

interface IStatusColors {
	backgroundColor: string;
	color: string;
	dotColor?: string;
}

const flexRowCenter = css`
	display: flex;
	flex-direction: row;
	align-items: center;
`;

export const WidgetErrorMessageWrap = styled.div`
	> div {
		padding: ${Css.pxToRem(42)} 0;

		@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
			padding: ${Css.pxToRem(90)} 0;
		}
	}
`;

export const ParkingWidgetContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Css.pxToRem(16)};
	background: ${theme.background?.default};
	border-radius: ${Css.pxToRem(16)};
	padding: ${Css.pxToRem(24)};
	box-shadow: ${Shadow.Large};

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		padding: ${Css.pxToRem(16)};
		gap: ${Css.pxToRem(16)};
	}
`;

export const WidgetTitle = styled.h3`
	${getHeadingStyle(theme, 'h6')}
	margin-bottom: 0;
`;

export const WidgetContent = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: space-between;

	@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
		flex-direction: column;
		gap: ${Css.pxToRem(8)};
	}
`;

export const WidgetContentBlock = styled.div`
	font-size: ${Css.pxToRem(18)};
	${flexRowCenter}
	gap: ${Css.pxToRem(16)};
	font-weight: 400;

	@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
		font-size: ${Css.pxToRem(16)};
		justify-content: space-between;
	}
`;

export const InfoBlock = styled.span<IStatusColors>`
	${flexRowCenter}
	gap: ${Css.pxToRem(10)};
	padding: ${Css.pxToRem(8)} ${Css.pxToRem(16)};
	border-radius: 8px;
	background-color: ${(props) => props.backgroundColor};
	color: ${(props) => props.color};

	@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
		min-width: 90px;
		justify-content: flex-end;
	}
`;

const InfoDot = styled.div<IStatusColors>`
	height: ${Css.pxToRem(8)};
	width: ${Css.pxToRem(8)};
	border-radius: 50%;
	background-color: ${(props) => props.dotColor};
`;
